import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Background, Controls, Handle, Position, ReactFlow, ReactFlowProvider, addEdge, applyEdgeChanges, applyNodeChanges, useReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toPng } from "html-to-image";
import api, { fmtErr, isDriveEmployee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowLeft, Diamond, FileImage, LayoutTemplate, Plus, Save, Square, StickyNote, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const COLORS = { slate: "#475569", sky: "#0284c7", emerald: "#059669", amber: "#d97706", rose: "#e11d48" };
const SHAPES = ["box", "diamond", "note"];

const TEMPLATES = [
  {
    key: "approval",
    nodes: [
      { shape: "box", label: "Submit document", color: "sky", x: 0, y: 0 },
      { shape: "box", label: "Consultant review", color: "slate", x: 0, y: 140 },
      { shape: "diamond", label: "Approved?", color: "amber", x: 0, y: 290 },
      { shape: "box", label: "Revise & resubmit", color: "rose", x: 260, y: 300 },
      { shape: "box", label: "Client approval", color: "emerald", x: 0, y: 470 },
    ],
    edges: [[0, 1], [1, 2], [2, 3], [3, 1], [2, 4]],
  },
  {
    key: "process",
    nodes: [
      { shape: "box", label: "Start", color: "sky", x: 0, y: 0 },
      { shape: "box", label: "Step 1", color: "slate", x: 0, y: 140 },
      { shape: "box", label: "Step 2", color: "slate", x: 0, y: 280 },
      { shape: "note", label: "Notes", color: "amber", x: 260, y: 140 },
      { shape: "box", label: "End", color: "emerald", x: 0, y: 420 },
    ],
    edges: [[0, 1], [1, 2], [2, 4]],
  },
];

function DiagramNode({ id, data, selected }) {
  const c = COLORS[data.color] || COLORS.slate;
  const ring = selected ? "ring-2 ring-sky-400" : "";
  const handles = (
    <>
      <Handle type="target" position={Position.Top} id="t-t" className="!h-2 !w-2 !bg-slate-400" />
      <Handle type="target" position={Position.Left} id="t-l" className="!h-2 !w-2 !bg-slate-400" />
      <Handle type="source" position={Position.Bottom} id="s-b" className="!h-2 !w-2 !bg-slate-400" />
      <Handle type="source" position={Position.Right} id="s-r" className="!h-2 !w-2 !bg-slate-400" />
    </>
  );
  if (data.shape === "diamond") {
    return (
      <div className="relative" style={{ width: 150, height: 150 }} data-testid={`dg-node-${id}`}>
        <div className={`absolute inset-0 rotate-45 rounded-md border-2 bg-white ${ring}`} style={{ borderColor: c }} />
        <div className="absolute inset-0 flex items-center justify-center px-7 text-center text-[11px] font-medium leading-tight text-slate-800">{data.label}</div>
        {handles}
      </div>
    );
  }
  if (data.shape === "note") {
    return (
      <div className={`flex min-h-[60px] min-w-[130px] items-center justify-center rounded-md border border-dashed bg-amber-50 px-3 py-2 text-center text-[12px] italic text-amber-900 shadow-sm ${ring}`} style={{ borderColor: COLORS.amber }} data-testid={`dg-node-${id}`}>
        {data.label}
        {handles}
      </div>
    );
  }
  return (
    <div className={`flex min-h-[56px] min-w-[150px] items-center justify-center rounded-lg border-2 bg-white px-3 py-2 text-center text-[12px] font-medium text-slate-800 shadow-sm ${ring}`} style={{ borderColor: c }} data-testid={`dg-node-${id}`}>
      <span className="mr-2 inline-block h-3 w-3 shrink-0 rounded-full" style={{ background: c }} />
      {data.label}
      {handles}
    </div>
  );
}

const nodeTypes = { dg: DiagramNode };

function DiagramEditor({ diagram, onBack }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes] = useState(() => (diagram.nodes || []).map((n) => ({ id: n.id, type: "dg", position: n.position, data: { shape: n.shape, label: n.label, color: n.color } })));
  const [edges, setEdges] = useState(() => (diagram.edges || []).map((e) => ({ id: e.id, source: e.source, target: e.target, type: "smoothstep" })));
  const [labelTarget, setLabelTarget] = useState(null);
  const [labelText, setLabelText] = useState("");
  const [color, setColor] = useState("slate");
  const [exporting, setExporting] = useState(false);
  const { askConfirm, confirmDialog } = useConfirm();

  const loadTemplate = (tpl) => {
    const stamp = Date.now();
    const ids = tpl.nodes.map((_, i) => `n-${stamp}-${i}`);
    setNodes(tpl.nodes.map((n, i) => ({ id: ids[i], type: "dg", position: { x: n.x, y: n.y }, data: { shape: n.shape, label: n.label, color: n.color } })));
    setEdges(tpl.edges.map(([a, b], i) => ({ id: `e-${stamp}-${i}`, source: ids[a], target: ids[b], type: "smoothstep" })));
  };
  const pickTemplate = (tpl) => (nodes.length ? askConfirm(t("studio.tplReplaceConfirm"), () => loadTemplate(tpl)) : loadTemplate(tpl));

  const onNodesChange = useCallback((c) => setNodes((ns) => applyNodeChanges(c, ns)), []);
  const onEdgesChange = useCallback((c) => setEdges((es) => applyEdgeChanges(c, es)), []);
  const onConnect = useCallback((c) => {
    if (c.source === c.target) return;
    setEdges((es) => addEdge({ ...c, type: "smoothstep" }, es));
  }, []);

  const addNode = (shape) => {
    const id = `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const pos = screenToFlowPosition({ x: window.innerWidth / 2 + Math.random() * 80 - 40, y: 320 + Math.random() * 80 });
    const label = t(shape === "diamond" ? "studio.addDecision" : shape === "note" ? "studio.addNote" : "studio.addBox");
    setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { id, type: "dg", position: pos, selected: true, data: { shape, label, color } }]);
  };

  const applyColor = (c) => {
    setColor(c);
    setNodes((ns) => ns.map((n) => (n.selected ? { ...n, data: { ...n.data, color: c } } : n)));
  };

  const saveMut = useMutation({
    mutationFn: async () =>
      api.put(`/studio/diagrams/${diagram.id}`, {
        nodes: nodes.map((n) => ({ id: n.id, shape: n.data.shape, label: n.data.label, color: n.data.color, position: n.position })),
        edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
      }),
    onSuccess: () => { toast.success(t("studio.dgSavedToast")); qc.invalidateQueries({ queryKey: ["studio-diagrams"] }); qc.invalidateQueries({ queryKey: ["studio-diagram", diagram.id] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const exportPng = async () => {
    setExporting(true);
    try {
      const el = document.querySelector(".dg-canvas .react-flow__viewport");
      const url = await toPng(el, { backgroundColor: "#ffffff", pixelRatio: 2, skipFonts: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `${diagram.name.replace(/\s+/g, "-")}.png`;
      a.click();
      toast.success(t("studio.exportDone"));
    } catch (e) { toast.error(fmtErr(e)); } finally { setExporting(false); }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-2" data-testid="dg-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onBack} data-testid="dg-back"><ArrowLeft className="mr-1 h-4 w-4" /> {t("studio.backToList")}</Button>
        <span className="font-display text-lg font-bold text-slate-900" data-testid="dg-name">{diagram.name}</span>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <Button size="sm" variant="outline" onClick={() => addNode("box")} data-testid="dg-add-box"><Square className="mr-1 h-4 w-4" /> {t("studio.addBox")}</Button>
        <Button size="sm" variant="outline" onClick={() => addNode("diamond")} data-testid="dg-add-diamond"><Diamond className="mr-1 h-4 w-4" /> {t("studio.addDecision")}</Button>
        <Button size="sm" variant="outline" onClick={() => addNode("note")} data-testid="dg-add-note"><StickyNote className="mr-1 h-4 w-4" /> {t("studio.addNote")}</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" data-testid="dg-templates"><LayoutTemplate className="mr-1 h-4 w-4" /> {t("studio.loadTemplate")}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent data-testid="dg-templates-menu">
            {TEMPLATES.map((tpl) => (
              <DropdownMenuItem key={tpl.key} onClick={() => pickTemplate(tpl)} data-testid={`dg-tpl-${tpl.key}`}>{t(`studio.tpl_${tpl.key}`)}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="mx-1 flex items-center gap-1.5" data-testid="dg-colors">
          <span className="text-xs text-slate-400">{t("studio.colorL")}</span>
          {Object.entries(COLORS).map(([k, v]) => (
            <button key={k} type="button" onClick={() => applyColor(k)} className={`h-5 w-5 rounded-full border-2 ${color === k ? "border-slate-900" : "border-white"}`} style={{ background: v }} data-testid={`dg-color-${k}`} />
          ))}
        </span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={exportPng} disabled={exporting || !nodes.length} data-testid="dg-export-png"><FileImage className="mr-1 h-4 w-4" /> {t("studio.exportPng")}</Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="dg-save"><Save className="mr-1 h-4 w-4" /> {t("studio.saveDiagram")}</Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">{t("studio.dblClickHint")}</p>
      <div className="dg-canvas min-h-0 flex-1 rounded-lg border bg-white" data-testid="dg-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDoubleClick={(e, n) => { setLabelTarget(n); setLabelText(n.data.label); }}
          deleteKeyCode={["Backspace", "Delete"]}
          fitView
          minZoom={0.2}
        >
          <Background gap={20} color="#e2e8f0" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <Dialog open={!!labelTarget} onOpenChange={(o) => !o && setLabelTarget(null)}>
        <DialogContent data-testid="dg-label-dialog">
          <DialogHeader><DialogTitle>{t("studio.editLabel")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("studio.editLabel")}</Label>
            <Input value={labelText} onChange={(e) => setLabelText(e.target.value)} data-testid="dg-label-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => { setNodes((ns) => ns.map((n) => (n.id === labelTarget.id ? { ...n, data: { ...n.data, label: labelText } } : n))); setLabelTarget(null); }} data-testid="dg-label-save">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}

export default function StudioDiagrams() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedId } = useProject();
  const qc = useQueryClient();
  const { askConfirm, confirmDialog } = useConfirm();
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const { data: diagrams = [], isLoading } = useQuery({
    queryKey: ["studio-diagrams", selectedId],
    queryFn: async () => (await api.get("/studio/diagrams", { params: { project_id: selectedId } })).data,
    enabled: !!selectedId,
  });
  const { data: openDiagram } = useQuery({
    queryKey: ["studio-diagram", openId],
    queryFn: async () => (await api.get(`/studio/diagrams/${openId}`)).data,
    enabled: !!openId,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/studio/diagrams", { project_id: selectedId, name: newName })).data,
    onSuccess: (d) => { toast.success(t("studio.dgCreatedToast")); setCreateOpen(false); setNewName(""); qc.invalidateQueries({ queryKey: ["studio-diagrams"] }); setOpenId(d.id); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/studio/diagrams/${id}`),
    onSuccess: () => { toast.success(t("studio.dgDeletedToast")); qc.invalidateQueries({ queryKey: ["studio-diagrams"] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  if (!isDriveEmployee(user)) return <Navigate to="/" replace />;

  if (openId) {
    return (
      <ReactFlowProvider>
        {openDiagram ? (
          <DiagramEditor key={openDiagram.id} diagram={openDiagram} onBack={() => setOpenId(null)} />
        ) : (
          <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
        )}
      </ReactFlowProvider>
    );
  }

  return (
    <div className="space-y-6" data-testid="studio-diagrams-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{t("studio.diagramsTitle")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("studio.diagramsSub")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="dg-new">
          <Plus className="mr-1.5 h-4 w-4" /> {t("studio.newDiagram")}
        </Button>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
      ) : diagrams.length === 0 ? (
        <p className="rounded-lg border border-dashed py-12 text-center text-sm text-slate-400" data-testid="dg-empty">{t("studio.emptyDiagrams")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {diagrams.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-lg border bg-white p-4" data-testid={`dg-card-${d.id}`}>
              <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setOpenId(d.id)} data-testid={`dg-open-${d.id}`}>
                <div className="truncate text-sm font-semibold text-slate-900">{d.name}</div>
                <div className="mt-0.5 font-mono text-[10px] text-slate-400">{(d.updated_at || "").slice(0, 16).replace("T", " ")}</div>
              </button>
              <Button variant="ghost" size="icon" className="text-rose-600" title={t("common.delete")} onClick={() => askConfirm(t("studio.deleteDiagramConfirm", { name: d.name }), () => deleteMut.mutate(d.id))} data-testid={`dg-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dg-create-dialog">
          <DialogHeader><DialogTitle>{t("studio.newDiagram")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("studio.diagramNamePh")}</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="dg-name-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => createMut.mutate()} disabled={!newName.trim() || createMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="dg-create-submit">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
